import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../contexts/AuthContext';
import { getFloc, getFlocsByParent, deleteFloc, deleteFlocWithDescendants, updateFloc, getAllDescendants, toggleFlocHiddenWithDescendants } from '../../services/flocService';
import { getAsset } from '../../services/assetService';
import { getInspectionsByFloc, Inspection } from '../../services/inspectionService';
import { generateFlocQRCode, printQRCodeLabel, downloadQRCode } from '../../services/qrCodeService';
import { uploadFlocLocationPhoto, uploadFlocDocument, deleteFile } from '../../services/storageService';
import { LocationPhotoEditor } from '../../components/LocationPhotoEditor';
import { RiskMatrixGrid } from '../../components/RiskMatrixGrid';
import { LocationFilterMap } from '../../components/LocationFilterMap';
import { MeasurementsWidget } from '../../components/MeasurementsWidget';
import { FunctionalLocation, Asset } from '@easydone/shared';
import { WrenchScrewdriverIcon, ChevronRightIcon, BuildingOfficeIcon, PencilIcon, TrashIcon, QrCodeIcon, MapPinIcon, PhotoIcon, DocumentIcon, ArrowDownTrayIcon, XMarkIcon, ClipboardDocumentCheckIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Timestamp } from 'firebase/firestore';

export function FlocDetailPage() {
  const { flocId } = useParams<{ flocId: string }>();
  const { currentOrg } = useOrg();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [floc, setFloc] = useState<FunctionalLocation | null>(null);
  const [parentFloc, setParentFloc] = useState<FunctionalLocation | null>(null);
  const [childFlocs, setChildFlocs] = useState<FunctionalLocation[]>([]);
  const [installedAsset, setInstalledAsset] = useState<Asset | null>(null);
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [descendantsCount, setDescendantsCount] = useState(0);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [markingLocation, setMarkingLocation] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadData();
  }, [flocId, currentOrg]);

  // Get current user location for map display
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        () => {
          // Silently fail if location is not available
        }
      );
    }
  }, []);

  const loadData = async () => {
    if (!flocId || !currentOrg) return;

    setLoading(true);
    setError('');

    try {
      const flocData = await getFloc(currentOrg.id, flocId);
      if (!flocData) {
        setError('Functional location not found');
        return;
      }
      setFloc(flocData);

      // Load parent FLOC if exists
      if (flocData.parentFlocId) {
        const parent = await getFloc(currentOrg.id, flocData.parentFlocId);
        setParentFloc(parent);
      }

      // Load child FLOCs
      const children = await getFlocsByParent(currentOrg.id, flocData.id);
      setChildFlocs(children);

      // Load installed asset if any
      if (flocData.installedAssetId) {
        const assetData = await getAsset(currentOrg.id, flocData.installedAssetId);
        setInstalledAsset(assetData);
      }

      // Load inspections for this FLOC
      const inspectionsData = await getInspectionsByFloc(currentOrg.id, flocData.id);
      // Sort by created date descending (newest first)
      const sorted = inspectionsData.sort((a, b) => {
        const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : (a.createdAt as any).seconds * 1000;
        const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : (b.createdAt as any).seconds * 1000;
        return bTime - aTime;
      });
      setInspections(sorted);
    } catch (err: any) {
      console.error('Failed to load FLOC:', err);
      setError('Failed to load functional location');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentOrg || !flocId) return;

    setDeleting(true);
    try {
      // Use cascading delete if there are descendants
      if (descendantsCount > 0) {
        const result = await deleteFlocWithDescendants(currentOrg.id, flocId);
        if (result.errors.length > 0) {
          setError(`Deleted ${result.deleted} locations, but encountered errors: ${result.errors.join(', ')}`);
        }
      } else {
        await deleteFloc(currentOrg.id, flocId);
      }
      navigate('/flocs');
    } catch (err: any) {
      setError(err.message || 'Failed to delete FLOC');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  // Load descendants count when showing delete dialog
  const handleShowDeleteDialog = async () => {
    if (!currentOrg || !flocId) return;

    try {
      const descendants = await getAllDescendants(currentOrg.id, flocId);
      setDescendantsCount(descendants.length);
      setShowDeleteConfirm(true);
    } catch (err: any) {
      setError('Failed to load descendants count');
    }
  };

  const handleGenerateQR = async () => {
    if (!currentOrg || !floc) return;
    try {
      const url = await generateFlocQRCode(floc.id);
      setQrCodeUrl(url);
      setShowQRModal(true);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      alert('Failed to generate QR code');
    }
  };

  const handleToggleHidden = async () => {
    if (!currentOrg || !floc || !currentUser) return;

    const newHiddenState = !floc.hidden;

    // Show confirmation if hiding and has children
    if (newHiddenState && (floc.childCount || 0) > 0) {
      const confirmed = confirm(
        `This will hide ${floc.name} and all ${floc.childCount} child location${floc.childCount !== 1 ? 's' : ''}. Continue?`
      );
      if (!confirmed) return;
    }

    try {
      const result = await toggleFlocHiddenWithDescendants(
        currentOrg.id,
        floc.id,
        newHiddenState,
        currentUser.uid
      );

      if (result.success) {
        // Reload the FLOC data to get the updated hidden state
        await loadData();

        const message = newHiddenState
          ? `Hidden ${result.affectedCount} location${result.affectedCount !== 1 ? 's' : ''} from standard views`
          : `Unhidden ${result.affectedCount} location${result.affectedCount !== 1 ? 's' : ''}`;

        alert(message);
      } else {
        throw new Error(result.error || 'Failed to update hidden status');
      }
    } catch (error: any) {
      console.error('Failed to toggle hidden status:', error);
      alert('Failed to update hidden status: ' + (error.message || 'Unknown error'));
    }
  };

  const handleMarkLocation = async () => {
    if (!currentOrg || !floc || !currentUser) return;

    setMarkingLocation(true);
    try {
      // Get current position
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by your browser'));
          return;
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
      });

      const location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };

      // Update FLOC with location
      await updateFloc(currentOrg.id, floc.id, { location }, currentUser.uid);

      // Reload data
      await loadData();

      alert('Location marked successfully!');
    } catch (error: any) {
      console.error('Failed to mark location:', error);
      alert(error.message || 'Failed to get location. Please ensure location permissions are enabled.');
    } finally {
      setMarkingLocation(false);
    }
  };

  const handleSaveLocationPhoto = async (
    file: File,
    overlayPosition: { x: number; y: number; width: number; height: number }
  ) => {
    if (!currentOrg || !floc || !currentUser) return;

    try {
      // Upload photo to storage
      const photoUrl = await uploadFlocLocationPhoto(currentOrg.id, floc.id, file);

      // Update FLOC with photo and overlay position
      await updateFloc(
        currentOrg.id,
        floc.id,
        {
          locationPhoto: {
            photoUrl,
            overlayPosition,
            capturedAt: Timestamp.now(),
          },
        },
        currentUser.uid
      );

      // Reload data
      await loadData();
    } catch (error) {
      console.error('Failed to save location photo:', error);
      throw error;
    }
  };

  const handleRemoveLocationPhoto = async () => {
    if (!currentOrg || !floc || !currentUser) return;

    try {
      // Delete photo from storage
      if (floc.locationPhoto?.photoUrl) {
        await deleteFile(floc.locationPhoto.photoUrl);
      }

      // Update FLOC to remove photo
      await updateFloc(
        currentOrg.id,
        floc.id,
        { locationPhoto: null as any },
        currentUser.uid
      );

      // Reload data
      await loadData();
    } catch (error) {
      console.error('Failed to remove location photo:', error);
      throw error;
    }
  };

  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !currentOrg || !floc || !currentUser) return;

    setUploadingDocument(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const url = await uploadFlocDocument(currentOrg.id, floc.id, file);
        return {
          id: `${Date.now()}-${Math.random()}`,
          name: file.name,
          url,
          type: file.type,
          size: file.size,
          uploadedAt: Timestamp.now(),
          uploadedBy: currentUser.uid,
        };
      });

      const newDocuments = await Promise.all(uploadPromises);

      // Update FLOC with new documents
      const updatedDocuments = [...(floc.documents || []), ...newDocuments];
      await updateFloc(
        currentOrg.id,
        floc.id,
        { documents: updatedDocuments },
        currentUser.uid
      );

      // Reload data
      await loadData();
      alert('Documents uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload documents:', error);
      alert('Failed to upload documents');
    } finally {
      setUploadingDocument(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!currentOrg || !floc || !currentUser) return;
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const document = floc.documents?.find((d) => d.id === documentId);
      if (document) {
        // Delete from storage
        await deleteFile(document.url);
      }

      // Update FLOC to remove document
      const updatedDocuments = floc.documents?.filter((d) => d.id !== documentId) || [];
      await updateFloc(
        currentOrg.id,
        floc.id,
        { documents: updatedDocuments },
        currentUser.uid
      );

      // Reload data
      await loadData();
      alert('Document deleted successfully!');
    } catch (error) {
      console.error('Failed to delete document:', error);
      alert('Failed to delete document');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getCriticalityColor = (criticality: string) => {
    switch (criticality) {
      case 'very_high': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-blue-100 text-blue-800';
      case 'very_low': return 'bg-green-100 text-green-800';
      case 'not_in_use': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error || !floc) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800">{error || 'Functional location not found'}</p>
          <button onClick={() => navigate('/flocs')} className="btn btn-secondary mt-4">
            Back to FLOCs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="card mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{floc.name}</h1>
              {floc.hidden && (
                <span className="px-2 py-1 rounded text-xs font-medium bg-gray-200 text-gray-700 flex items-center">
                  <EyeSlashIcon className="w-3 h-3 mr-1" />
                  Hidden
                </span>
              )}
            </div>
            <p className="text-gray-600 mt-1">{floc.code}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCriticalityColor(floc.criticality)}`}>
            {floc.criticality.toUpperCase()}
          </span>
        </div>

        {floc.description && (
          <p className="text-gray-700 mb-4">{floc.description}</p>
        )}

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Type:</span>
            <span className="ml-2 font-medium capitalize">{floc.type.replace('_', ' ')}</span>
          </div>
          <div>
            <span className="text-gray-600">Status:</span>
            <span className="ml-2 font-medium capitalize">{floc.operationalStatus.replace('_', ' ')}</span>
          </div>
          {floc.functionalSystem && (
            <div>
              <span className="text-gray-600">System:</span>
              <span className="ml-2 font-medium">{floc.functionalSystem}</span>
            </div>
          )}
          {floc.level !== undefined && (
            <div>
              <span className="text-gray-600">Hierarchy Level:</span>
              <span className="ml-2 font-medium">{floc.level}</span>
            </div>
          )}
        </div>
      </div>

      {/* Risk Assessment Matrix */}
      {floc.conditionScore && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-4">Risk Assessment</h2>
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Criticality:</span>
                <span className="ml-2 font-semibold text-gray-900 capitalize">{floc.criticality} ({(() => {
                  const critValues: Record<string, number> = { low: 2, medium: 3, high: 4, critical: 5 };
                  return critValues[floc.criticality] || 2;
                })()})</span>
              </div>
              <div>
                <span className="text-gray-600">Condition:</span>
                <span className="ml-2 font-semibold text-gray-900">{floc.conditionScore} - {(() => {
                  const labels: Record<number, string> = {
                    1: 'Very good',
                    2: 'Good',
                    3: 'Moderate',
                    4: 'Poor',
                    5: 'Very poor'
                  };
                  return labels[floc.conditionScore] || 'Unknown';
                })()}</span>
              </div>
              <div>
                <span className="text-gray-600">Risk Score:</span>
                <span className="ml-2 font-semibold text-gray-900">{(() => {
                  const critValues: Record<string, number> = { low: 2, medium: 3, high: 4, critical: 5 };
                  return (critValues[floc.criticality] || 2) * floc.conditionScore;
                })()}</span>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            The star below shows this FLOC's current position on the risk matrix based on its criticality and condition assessment.
          </p>
          <RiskMatrixGrid
            riskMatrix={currentOrg?.settings?.riskMatrix}
            highlightedFloc={{
              criticality: floc.criticality,
              conditionScore: floc.conditionScore
            }}
          />
        </div>
      )}

      {/* Measurements */}
      <div className="mb-6">
        <MeasurementsWidget
          entityType="floc"
          entityId={floc.id}
          entityName={floc.name}
        />
      </div>

      {/* Hierarchy Navigation */}
      {(parentFloc || childFlocs.length > 0) && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <BuildingOfficeIcon className="w-5 h-5 mr-2" />
            Hierarchy
          </h2>

          {/* Parent FLOC */}
          {parentFloc && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Parent Location:</p>
              <button
                onClick={() => navigate(`/flocs/${parentFloc.id}`)}
                className="w-full text-left bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg p-3 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{parentFloc.name}</p>
                    <p className="text-sm text-gray-600">{parentFloc.code}</p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                </div>
              </button>
            </div>
          )}

          {/* Child FLOCs */}
          {childFlocs.length > 0 && (
            <div>
              <p className="text-sm text-gray-600 mb-2">
                Child Locations ({childFlocs.length}):
              </p>
              <div className="space-y-2">
                {childFlocs.map((child) => (
                  <button
                    key={child.id}
                    onClick={() => navigate(`/flocs/${child.id}`)}
                    className="w-full text-left bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg p-3 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{child.name}</p>
                        <p className="text-sm text-gray-600">{child.code}</p>
                      </div>
                      <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Inspection History */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <ClipboardDocumentCheckIcon className="w-5 h-5 mr-2" />
          Inspection History
        </h2>
        {inspections.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-600">No inspections recorded for this FLOC</p>
          </div>
        ) : (
          <div className="space-y-2">
            {inspections.slice(0, 5).map((inspection) => {
              const createdDate = inspection.createdAt instanceof Date
                ? inspection.createdAt
                : new Date((inspection.createdAt as any).seconds * 1000);
              const statusColors: Record<string, string> = {
                draft: 'bg-gray-100 text-gray-800',
                scheduled: 'bg-purple-100 text-purple-800',
                in_progress: 'bg-blue-100 text-blue-800',
                completed: 'bg-green-100 text-green-800',
                pending_review: 'bg-yellow-100 text-yellow-800',
                approved: 'bg-green-100 text-green-800',
                rejected: 'bg-red-100 text-red-800',
                canceled: 'bg-red-100 text-red-800',
                failed: 'bg-red-100 text-red-800',
                archived: 'bg-gray-100 text-gray-800',
              };

              return (
                <button
                  key={inspection.id}
                  onClick={() => navigate(`/inspections/${inspection.id}`)}
                  className="w-full text-left bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg p-3 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-900 truncate">
                          {inspection.activityClass === 'inspection' ? 'Inspection' : 'Maintenance'} - Template {inspection.templateId.substring(0, 8)}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded ${statusColors[inspection.status] || 'bg-gray-100 text-gray-800'}`}>
                          {inspection.status.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {inspection.activityClass === 'inspection' ? 'Inspection' : 'Maintenance'} • {createdDate.toLocaleDateString()}
                      </p>
                      {inspection.targetType === 'asset' && (
                        <p className="text-xs text-gray-500 mt-1">Asset Inspection</p>
                      )}
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
                  </div>
                </button>
              );
            })}
            {inspections.length > 5 && (
              <button
                onClick={() => navigate(`/inspections?flocId=${floc.id}`)}
                className="w-full text-center text-sm text-primary-600 hover:text-primary-700 py-2"
              >
                View all {inspections.length} inspections →
              </button>
            )}
          </div>
        )}
      </div>

      {/* Location */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <MapPinIcon className="w-5 h-5 mr-2" />
          Location
        </h2>
        {floc.location ? (
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-3">
              <p className="text-sm text-gray-600 mb-2">GPS Coordinates:</p>
              <p className="font-mono text-sm">
                Lat: {floc.location.latitude.toFixed(6)}, Long: {floc.location.longitude.toFixed(6)}
              </p>
            </div>
            <LocationFilterMap
              items={[]}
              locationEnabled={false}
              currentLocation={currentLocation}
              locationRadius={0}
              locationError=""
              onEnableLocation={() => {}}
              onDisableLocation={() => {}}
              onRadiusChange={() => {}}
              renderMarkerPopup={() => null}
              onItemClick={() => {}}
              showEmbedded={true}
              centerOnLocation={floc.location}
            />
            <button
              onClick={handleMarkLocation}
              disabled={markingLocation}
              className="btn btn-secondary text-sm mt-3"
            >
              {markingLocation ? 'Pinning Location...' : 'Update Location'}
            </button>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-600 mb-3">No location marked for this FLOC</p>
            <button
              onClick={handleMarkLocation}
              disabled={markingLocation}
              className="btn btn-primary text-sm"
            >
              {markingLocation ? 'Pinning Location...' : 'Pin Here'}
            </button>
          </div>
        )}
      </div>

      {/* Location Photo */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <PhotoIcon className="w-5 h-5 mr-2" />
          Location Photo
        </h2>
        <LocationPhotoEditor
          photoUrl={floc.locationPhoto?.photoUrl}
          overlayPosition={floc.locationPhoto?.overlayPosition}
          onSave={handleSaveLocationPhoto}
          onRemove={handleRemoveLocationPhoto}
        />
      </div>

      {/* Documents */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <DocumentIcon className="w-5 h-5 mr-2" />
          Documents & Attachments
        </h2>

        {/* Upload Section */}
        <div className="mb-4">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleDocumentUpload}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingDocument}
            className="btn btn-primary text-sm"
          >
            {uploadingDocument ? 'Uploading...' : 'Upload Documents'}
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Supported: PDF, Word, Excel, PowerPoint, images, and more
          </p>
        </div>

        {/* Documents List */}
        {floc.documents && floc.documents.length > 0 ? (
          <div className="space-y-2">
            {floc.documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
              >
                <div className="flex items-center flex-1 min-w-0">
                  <DocumentIcon className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{doc.name}</p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(doc.size)} • {new Date(doc.uploadedAt instanceof Date ? doc.uploadedAt : (doc.uploadedAt as any).seconds * 1000).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary text-sm flex items-center"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                    Download
                  </a>
                  <button
                    onClick={() => handleDeleteDocument(doc.id)}
                    className="btn bg-red-600 hover:bg-red-700 text-white text-sm"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 bg-gray-50 border border-gray-200 rounded-lg">
            <DocumentIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600">No documents uploaded yet</p>
          </div>
        )}
      </div>

      {/* Installed Asset */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <WrenchScrewdriverIcon className="w-5 h-5 mr-2" />
          Installed Asset
        </h2>
        {installedAsset ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{installedAsset.name}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Serial: {installedAsset.serialNumber} | Type: {installedAsset.type}
                </p>
                <div className="mt-2">
                  <span className="text-sm font-medium">Condition: </span>
                  <span className={`text-lg font-bold ${
                    installedAsset.conditionScore >= 80 ? 'text-green-600' :
                    installedAsset.conditionScore >= 60 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {installedAsset.conditionScore}
                  </span>
                  <span className="ml-2 text-xs px-2 py-0.5 rounded bg-gray-200">
                    Grade {installedAsset.conditionGrade}
                  </span>
                </div>
              </div>
              <button
                onClick={() => navigate(`/assets/${installedAsset.id}`)}
                className="btn btn-secondary text-sm"
              >
                View Asset
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-gray-600">No asset installed at this location</p>
            <button
              onClick={() => navigate(`/flocs/${floc.id}/commission`)}
              className="btn btn-primary mt-4 text-sm"
            >
              Commission Asset to this FLOC
            </button>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button onClick={() => navigate('/flocs')} className="btn btn-secondary">
          Back to FLOCs
        </button>
        <button
          onClick={() => navigate(`/flocs/${floc.id}/edit`)}
          className="btn btn-secondary flex items-center"
        >
          <PencilIcon className="w-4 h-4 mr-2" />
          Edit
        </button>
        <button
          onClick={handleShowDeleteDialog}
          className="btn bg-red-600 hover:bg-red-700 text-white flex items-center"
        >
          <TrashIcon className="w-4 h-4 mr-2" />
          Delete
        </button>
        <button
          onClick={handleGenerateQR}
          className="btn btn-secondary flex items-center"
        >
          <QrCodeIcon className="w-4 h-4 mr-2" />
          QR Code
        </button>
        <button
          onClick={handleToggleHidden}
          className="btn btn-secondary flex items-center"
          title={floc.hidden ? 'Show in standard views' : 'Hide from standard views'}
        >
          {floc.hidden ? (
            <>
              <EyeIcon className="w-4 h-4 mr-2" />
              Unhide
            </>
          ) : (
            <>
              <EyeSlashIcon className="w-4 h-4 mr-2" />
              Hide
            </>
          )}
        </button>
        <button
          onClick={() => navigate(`/inspections/new?flocId=${floc.id}`)}
          className="btn btn-primary ml-auto"
        >
          New Inspection
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Delete Functional Location?</h2>
            <p className="text-gray-700 mb-4">
              Are you sure you want to delete <strong>{floc.name}</strong>?
            </p>

            {/* Warning for descendants */}
            {descendantsCount > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800 font-medium mb-2">
                  Warning: This will also delete {descendantsCount} descendant location{descendantsCount !== 1 ? 's' : ''}!
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  All child locations will be permanently deleted along with this location.
                </p>
              </div>
            )}

            {/* Error for installed assets */}
            {installedAsset && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800 font-medium mb-2">Cannot delete this FLOC:</p>
                <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>Has an installed asset - remove the asset before deleting</li>
                </ul>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="btn bg-red-600 hover:bg-red-700 text-white"
                disabled={deleting || !!installedAsset}
              >
                {deleting ? 'Deleting...' : descendantsCount > 0 ? `Delete All (${descendantsCount + 1})` : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && qrCodeUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-bold text-gray-900 mb-4">FLOC QR Code</h2>
            <div className="flex justify-center mb-4">
              <img src={qrCodeUrl} alt="QR Code" className="w-64 h-64" />
            </div>
            <div className="text-center mb-4">
              <p className="font-medium text-gray-900">{floc.code}</p>
              <p className="text-sm text-gray-600">{floc.name}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => printQRCodeLabel(qrCodeUrl, floc.code, floc.name)}
                className="btn btn-primary flex-1"
              >
                Print
              </button>
              <button
                onClick={() => downloadQRCode(qrCodeUrl, floc.code)}
                className="btn btn-secondary flex-1"
              >
                Download
              </button>
              <button
                onClick={() => setShowQRModal(false)}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
